import {
  type Api,
  type Candidate,
  type Chat,
  type CountryCode,
  formatPhone,
  type ImportItem,
  type MatchOptions,
  type PublicUser,
  regionFromLocale,
  type SenderMatch,
  senderEmail,
  senderPhone,
  suggestMatches,
  suggestTarget,
} from "@rhema/client";
import { Button, Select, type SelectOption, Sheet, TextField } from "@rhema/ui";
import { type FormEvent, useEffect, useId, useMemo, useState } from "react";
import { UpgradeLink, useFailure } from "../billing/UpgradeLink";
import { canPost, chatView } from "../data/view";
import { useContacts } from "../people/useContacts";
import { useFeatures } from "../session/features";
import { useApi, useClient, useClientState, useMe } from "../session/session";
import { useImportQueue } from "./ImportProvider";

const NAME_ONLY = "";

/** Lookups for senders WhatsApp showed as numbers or email addresses, at most this many per export. */
const MAX_LOOKUPS = 20;

type Lookups = { people: PublicUser[]; bySender: NonNullable<MatchOptions["found"]> };

/**
 * Asks the server who has each number (when phone numbers are on) or each exact email address
 * (when they are off). Someone not on Rhema, or not findable by their number, is simply not found.
 */
export async function lookUp(
  api: Api,
  senders: string[],
  phone: boolean,
  region: CountryCode,
): Promise<Lookups> {
  const out: Lookups = { people: [], bySender: {} };
  let asked = 0;
  for (const sender of senders) {
    if (asked >= MAX_LOOKUPS) break;
    const number = phone ? senderPhone(sender, region) : null;
    const email = phone ? null : senderEmail(sender);
    if (!number && !email) continue;
    asked += 1;
    try {
      if (number) {
        const person = await api.userByPhone(number);
        out.people.push(person);
        out.bySender[sender] = { userId: person.id, reason: "phone" };
      } else if (email) {
        const list = await api.findByEmail(email);
        if (list.length === 1) {
          out.people.push(list[0]!);
          out.bySender[sender] = { userId: list[0]!.id, reason: "email" };
        }
      }
    } catch {
      // Not on Rhema, or the server said no. The sender stays a name the person can match by hand.
    }
  }
  return out;
}

/**
 * Matches the export's senders to people on Rhema and picks the chat to import into. A sender can
 * stay a name only. Nothing is sent until the person starts the import.
 */
export function ImportSetup({ item, onClose }: { item: ImportItem; onClose: () => void }) {
  const client = useClient();
  const api = useApi();
  const queue = useImportQueue();
  const me = useMe();
  const state = useClientState();
  const features = useFeatures();
  const loaded = useContacts();
  // Without contacts the person can still keep every sender as a name.
  const contacts = loaded.contacts ?? (loaded.error ? [] : null);
  const titleId = useId();
  const { error, upgrade, fail, clear } = useFailure();
  const [found, setFound] = useState<Lookups | null>(null);
  const [matches, setMatches] = useState<Record<string, SenderMatch> | null>(null);
  const [target, setTarget] = useState<"new" | "existing">("new");
  const [chatId, setChatId] = useState("");
  const [title, setTitle] = useState(item.title);
  const [busy, setBusy] = useState(false);

  const phoneOn = features?.phone === true;
  const region = useMemo(
    () => regionFromLocale(typeof navigator === "undefined" ? null : navigator.language),
    [],
  );
  const self = useMemo<Candidate>(
    () => ({ id: me.id, display_name: me.display_name, phone: me.phone ?? null }),
    [me.id, me.display_name, me.phone],
  );
  const people = useMemo(() => {
    const byId = new Map<string, Candidate>();
    for (const p of [...(contacts ?? []), ...(found?.people ?? [])])
      if (p.id !== me.id && !byId.has(p.id)) byId.set(p.id, p);
    return [...byId.values()].sort((a, b) => a.display_name.localeCompare(b.display_name));
  }, [contacts, found, me.id]);
  const senderOptions = useMemo<SelectOption[]>(
    () => [
      { value: NAME_ONLY, label: "Keep as a name only" },
      { value: me.id, label: "You" },
      ...people.map((p) => ({
        value: p.id,
        label: p.display_name,
        ...(p.phone ? { detail: formatPhone(p.phone), keywords: p.phone } : {}),
      })),
    ],
    [people, me.id],
  );

  // WhatsApp shows a number, or rarely an email address, for people the exporter had not saved.
  // With phone numbers on, Rhema looks the number up. With them off, it looks up an exact email.
  useEffect(() => {
    if (!features) return;
    let live = true;
    void lookUp(api, item.senders, features.phone === true, region).then((result) => {
      if (live) setFound(result);
    });
    return () => {
      live = false;
    };
  }, [api, features, item.senders, region]);

  // Suggest once everything has loaded, and keep the person's own choices after that.
  useEffect(() => {
    if (contacts === null || found === null || matches) return;
    setMatches(suggestMatches(item.senders, people, self, { phone: phoneOn, region, found: found.bySender }));
  }, [contacts, found, people, matches, item.senders, self, phoneOn, region]);

  const chats = useMemo(
    () =>
      state.chatOrder
        .map((id) => state.chats[id])
        .filter((c): c is Chat => Boolean(c && c.kind !== "assistant" && !c.archived && canPost(c))),
    [state],
  );

  const current = matches ?? {};
  const suggestion = suggestTarget(item.senders, current, me.id);
  const nameOf = (id: string) =>
    id === me.id ? "You" : (people.find((p) => p.id === id)?.display_name ?? "Someone");

  const start = async (e: FormEvent) => {
    e.preventDefault();
    if (!matches) return;
    setBusy(true);
    clear();
    try {
      let id = chatId;
      if (target === "new") {
        const chat =
          suggestion.kind === "direct"
            ? await client.createDirect(suggestion.userId)
            : await client.createGroup(title.trim() || item.title, suggestion.memberIds);
        id = chat.id;
      }
      if (!id) return;
      await queue.start(item.id, { matches, chatId: id });
      onClose();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title={item.title}
      subtitle="Choose who is who and where it goes"
      className="px-5 pb-7"
    >
      <form onSubmit={start} className="mt-3 flex flex-col gap-4">
        <section aria-labelledby={titleId} className="flex flex-col gap-2">
          <h2 id={titleId} className="m-0 text-[15px] font-semibold">
            People in this chat
          </h2>
          <p className="m-0 text-[13px] text-ink-secondary">
            Match each name to someone on Rhema, or keep it as a name only. Imported messages always show the
            name and a mark saying they came from WhatsApp.
          </p>
          {item.senders.length === 0 && (
            <p className="m-0 text-sm text-ink-secondary">No one wrote in this chat.</p>
          )}
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {item.senders.map((sender) => (
              <li key={sender} className="flex flex-col gap-1">
                <span className="truncate text-sm font-semibold">{sender}</span>
                <Select
                  label={`Who is ${sender}`}
                  hideLabel
                  value={current[sender]?.userId ?? NAME_ONLY}
                  options={senderOptions}
                  searchable={senderOptions.length > 8}
                  searchPlaceholder="Search people"
                  onChange={(value) =>
                    setMatches({
                      ...current,
                      [sender]: { userId: value || null, reason: null },
                    })
                  }
                  className="w-full"
                />
              </li>
            ))}
          </ul>
        </section>

        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="mb-2 p-0 text-[15px] font-semibold">Import into</legend>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" name="target" checked={target === "new"} onChange={() => setTarget("new")} />
            {suggestion.kind === "direct"
              ? `A chat with ${nameOf(suggestion.userId)}`
              : suggestion.memberIds.length
                ? `A new group with ${suggestion.memberIds.map(nameOf).join(", ")}`
                : "A new group"}
          </label>
          {target === "new" && suggestion.kind === "group" && (
            <TextField
              label="Group name"
              maxLength={80}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="target"
              checked={target === "existing"}
              onChange={() => setTarget("existing")}
              disabled={!chats.length}
            />
            A chat you already have
          </label>
          {target === "existing" && (
            <Select
              label="Chat"
              hideLabel
              value={chatId}
              options={[
                { value: "", label: "Choose a chat" },
                ...chats.map((c) => ({ value: c.id, label: chatView(c, state).title })),
              ]}
              searchable={chats.length > 8}
              searchPlaceholder="Search chats"
              onChange={setChatId}
              className="w-full"
            />
          )}
        </fieldset>

        <p className="m-0 text-[13px] text-ink-secondary">
          Everyone in the chat will see this history. It is end-to-end encrypted like any message, and a line
          in the chat says you imported it.
        </p>
        {error && (
          <p role="alert" className="m-0 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-sm text-scarlet">
            {error}
            {upgrade && <UpgradeLink />}
          </p>
        )}
        <Button type="submit" size="lg" disabled={busy || !matches || (target === "existing" && !chatId)}>
          {busy ? "Starting" : `Import ${item.total.toLocaleString("en-GB")} messages`}
        </Button>
      </form>
    </Sheet>
  );
}
